import React from 'react';
import DefaultImage from '@/assets/images/defaultImage.jpg';
import { ImageResponse, ResourceLocation } from '@/generated/data.schemas';
import { Button, ButtonVariant } from '../Button';
import { Icon, IconName } from '../Icon';
import { Image } from '../Image';
import { Link } from '../Link';
import styles from './TeaserCard.module.scss';

interface TeaserCardProps {
	image: Partial<ImageResponse>;
	responsiveSizesSettings?: string;
	altText?: string;
	text?: string;
	title: string;
	icon?: IconName;
	url?: string | null;
	ctaLabel?: string;
	hideCtaLabel?: boolean;
	route?: ResourceLocation;
	buttonVariant?: ButtonVariant;
}

/**
 * Generic TeaserCard component, used for creating cards with teaser content, e.g. news, articles, etc.
 */
export const TeaserCard: React.FunctionComponent<TeaserCardProps> = ({
	image,
	responsiveSizesSettings,
	altText,
	text,
	title,
	icon,
	url,
	ctaLabel,
	hideCtaLabel,
	route,
	buttonVariant,
}) => {
	const imageObj = {
		size180: image.size180 ? image.size180 : { url: DefaultImage },
		size320: image.size320 ? image.size320 : undefined,
		size480: image.size480 ? image.size480 : undefined,
		size720: image.size720 ? image.size720 : undefined,
		size960: image.size960 ? image.size960 : undefined,
		size1280: image.size1280 ? image.size1280 : undefined,
		size1920: image.size1920 ? image.size1920 : undefined,
	};

	return (
		<Link className={styles.wrapper} linkUrl={url || ''} target="_self" route={route || undefined}>
			<div className={styles.imgWrapper}>
				{image && (
					<Image
						className={styles.img}
						{...imageObj}
						responsiveSizesSettings={responsiveSizesSettings || '50vw'}
						altText={image.altText || altText || ''}
					/>
				)}
			</div>
			<div className={styles.contentWrapper}>
				<div className={styles.titleWrapper}>
					{icon && <Icon name={icon} size={'md'} />}
					<h4 className={styles.title}>{title}</h4>
				</div>
				{text && <p className={styles.text}>{text}</p>}
				{url && ctaLabel ? (
					<Button
						className={styles.cta}
						variant={buttonVariant ?? 'secondary'}
						aria-label={ctaLabel}
						hasOnlyIcon={hideCtaLabel}
					>
						{hideCtaLabel ? <Icon name={'arrowLeft'} rotate={'180'} size={'md'} /> : ctaLabel}
					</Button>
				) : null}
			</div>
		</Link>
	);
};
